import type { ReactElement } from 'react';

import type * as api from '@gitbook/api-client';
import { Alert } from '@gitbook/spine-react';

import { hasAnyGitSync, hasGitSyncInstallation } from '../../GitSync';

/**
 * Whether linking `space` into `site` would hand the space's Git Sync over to the site: the space
 * has any Git Sync (including legacy repo-only installs) and the site has an installation that
 * governs its member spaces. Exported so callers that gate UI on the same condition (e.g. a confirm
 * dialog before an immediate add) stay in sync with the alert.
 */
export function shouldWarnSpaceGitSyncSiteOverride(
    space: api.Space | null | undefined,
    site: api.Site
): boolean {
    return hasAnyGitSync(space) && hasGitSyncInstallation(site);
}

/**
 * Warn that linking a Git-synced space into a Git-synced site hands its Git Sync over to the site:
 * the space starts following the site's Git Sync settings (and repository) instead of its own, which
 * replaces its repo connection — its own content can become unsynced. Space-level settings like the
 * project directory are kept.
 *
 * Renders nothing unless both the space and the site already have a Git Sync installation, so it's
 * safe to drop into any space → site linking surface (forms, confirm dialogs).
 */
export function SpaceGitSyncSiteOverrideAlert(props: {
    space: api.Space | null | undefined;
    site: api.Site;
    className?: string;
}): ReactElement | null {
    const { space, site, className } = props;

    if (!shouldWarnSpaceGitSyncSiteOverride(space, site)) {
        return null;
    }

    return (
        <Alert
            kind="warning"
            size="small"
            className={className}
            title="Git Sync will be overridden"
            details={`When added to the site, this space's content will sync to the git repository and branch of “${site.title}”.`}
        />
    );
}
